import { DomainPreferences, SQLFilters } from '../../../../api/types';
import {
  EmployeeCohortMetricId,
  RegularMetricId,
} from '../../../../api/types-graphql';
import { LanguageString } from '../../../../common/components/dashboards/types';
import {
  BenchmarkPeriods,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { OverTimeUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { OverTimeSegmentedVizViews } from '../../../../common/components/data-viz-containers/overtime-segmented-query/types';
import { TimePeriodSegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-segmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../../common/components/storyboard-components/common/types';
import { Months } from '../../../../common/components/timeslider/types';
import { Languages } from '../../../../constants';
import {
  addQuarter,
  getFiscalQuarter,
  getFiscalYear,
  getMomentDate,
} from '../../../../date/date-utils';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  TimeSelectionType,
} from '../../../../types';
import { getKeyFromDataFieldWithDataType } from '../../../../utils';
import { StoryboardDateUtils } from '../../utils';

export const getTurnoverByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  dimension: DataFieldWithDataType,
  title: LanguageString,
  filters: SQLFilters | null,
  layoutConfig: {
    gridRowSpan?: number;
    gridColSpan?: number;
    gridColStart?: number;
    gridColEnd?: number;
    gridRowStart?: number;
    gridRowEnd?: number;
  }
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: `by-dimension-chart-${getKeyFromDataFieldWithDataType(dimension)}`,
    layoutConfig,
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0318_3CustomAttritionNikkiTtm,
            },
          },
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0318_4CustomAttritionNikkiLeavers,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: dimension,
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getGrossProfitPerHeadOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const threeQuartersAgo = addQuarter(
    getMomentDate(nowEndOfMonth).valueOf(),
    -3
  );
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentQuarter = {
    quarterOfYear: getFiscalQuarter(nowDate, finYearStartMonth),
    year: getFiscalYear(nowDate, finYearStartMonth),
  };
  const threeQuartersAgoQuarter = {
    quarterOfYear: getFiscalQuarter(threeQuartersAgo, finYearStartMonth),
    year: getFiscalYear(threeQuartersAgo, finYearStartMonth),
  };
  return {
    chartId: 'gross-profit-per-head-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Gross Profit Per Head (Consolidated)',
          [Languages.JA]: '1人あたり粗利（全社） ',
        },
        queries: [
          {
            metric: {
              type: 'EmployeeCohortMetricId',
              value: EmployeeCohortMetricId.Pm0453_1GrossProfitMonthlyPerIndiv,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearQuarterly,
          input: {
            start: threeQuartersAgoQuarter,
            end: currentQuarter,
          },
        },
        benchmarkConfig: {
          benchmarkPeriod: BenchmarkPeriods.YEAR_ON_YEAR,
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getKeyTalentAttritionOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'key-talent-attrition-overtime-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Key Talent Attrition (Consolidated)',
          [Languages.JA]: 'ハイパフォーマー離職率（事業部別）',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0080_3RegretAttritionTtm,
          },
          filters: `EMPLOYEE.CUSTOM_FILTER_8 = '1' OR EMPLOYEE.CUSTOM_FILTER_8 = '2'`,
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
          },
        },
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getGrossProfitPerHeadByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentQuarter = {
    quarterOfYear: getFiscalQuarter(nowDate, finYearStartMonth),
    year: getFiscalYear(nowDate, finYearStartMonth),
  };
  return {
    chartId: 'gross-profit-per-head-by-organization-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Gross Profit Per Head (By Business Segment)',
          [Languages.JA]: '1人あたり粗利（事業部別）',
        },
        queries: [
          {
            metric: {
              type: 'EmployeeCohortMetricId',
              value: EmployeeCohortMetricId.Pm0453_1GrossProfitMonthlyPerIndiv,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByQuarters,
          input: {
            start: currentQuarter,
            end: currentQuarter,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
          },
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getKeyTalentAttritionByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentQuarter = {
    quarterOfYear: getFiscalQuarter(nowDate, finYearStartMonth),
    year: getFiscalYear(nowDate, finYearStartMonth),
  };
  return {
    chartId: 'key-talent-attrition-by-organization-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Key Talent Attrition (By Business Segment)',
          [Languages.JA]: 'ハイパフォーマー分布（事業部別）',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0001_1IndividualsCount,
          },
          timeSelection: {
            type: TimeSelectionType.FinancialYearSingleValueByQuarters,
            input: {
              start: currentQuarter,
              end: currentQuarter,
            },
          },
          groupBy: {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
            },
          },
          segmentBy: {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.CUSTOM_FILTER_8,
            },
          },
        },
      },
      selectedView: TimePeriodSegmentedVizViews.BAR_CHART,
    },
  };
};

export const getWorkforceFulfillmentRateFactboxConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: `${EmployeeCohortMetricId.Pm0286_2PlannedIndividualsPercHc}-factbox`,
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '[Org Agility] Workforce Fulfillment Rate',
          [Languages.JA]: '[変革実行力] 人材ポートフォリオ充足率',
        },
        subtitle: {
          [Languages.EN]: '(%)',
          [Languages.JA]: '(%)',
        },
        section: {
          query: {
            metric: {
              type: 'EmployeeCohortMetricId',
              value: EmployeeCohortMetricId.Pm0286_2PlannedIndividualsPercHc,
            },
            segmentBy: {
              type: 'non-hierarchical',
              dataField: {
                dataType: DataTypes.EMPLOYEE,
                dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
              },
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: { start: nowEndOfMonth, end: nowEndOfMonth },
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getTenureInJobGradeFactboxConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: `${RegularMetricId.Pm0163TenureInJobgradeAvg}-factbox`,
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '[Org Agility] Tenure in Jobgrade',
          [Languages.JA]: '[変革実行力] 同一グレードでの滞在期間',
        },
        subtitle: {
          [Languages.EN]: '(◯年◯ヶ月）',
          [Languages.JA]: '(◯年◯ヶ月）',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0163TenureInJobgradeAvg,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: { start: nowEndOfMonth, end: nowEndOfMonth },
            },
            segmentBy: {
              type: 'non-hierarchical',
              dataField: {
                dataType: DataTypes.EMPLOYEE,
                dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
              },
              filters: ['G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7'],
            },
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getRemoteWorkDayOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'remote-work-day-overtime-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '[Flexibility] Remote Work Days',
          [Languages.JA]: '[柔軟性] リモートワーク日数',
        },
        subtitle: {
          [Languages.EN]: '（月間、日）',
          [Languages.JA]: '（月間、日）',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0219_3DaysRemoteAvg,
          },
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
          },
        },
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getOvertimeWorkOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'overtime-work-overtime-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_SEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '[Flexibility] Overtime',
          [Languages.JA]: '[柔軟性] 残業時間',
        },
        subtitle: {
          [Languages.EN]: '（月間、時間）',
          [Languages.JA]: '（月間、時間）',
        },
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0215_2MonthlyOtSumPerHcWithOt,
          },
        },
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
          },
        },
      },
      selectedView: OverTimeSegmentedVizViews.LINE_CHART,
    },
  };
};

export const getTrainingInvestmentPerHeadByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentFinYear = getFiscalYear(nowDate, finYearStartMonth);
  return {
    chartId: 'training-investment-per-head-by-organization-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '[Long-term Vision] Training Investment Per Head',
          [Languages.JA]: '[長期志向] 人材育成投資額',
        },
        queries: [
          {
            metric: {
              type: 'EmployeeCohortMetricId',
              value: EmployeeCohortMetricId.Pm0455_1TrainingCostMonthlyPerIndiv,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: {
            start: currentFinYear,
            end: currentFinYear,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
          },
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getTrainingUtilizationByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentFinYear = getFiscalYear(nowDate, finYearStartMonth);
  return {
    chartId: 'training-utilization-by-organization-chart',
    layoutConfig: {
      gridRowSpan: 1,
      gridColSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: '[Long-term Vision] Training Utilization Rate',
          [Languages.JA]: '[長期志向] 研修受講率',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0173_2TraineePercHc,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: {
            start: currentFinYear,
            end: currentFinYear,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
          },
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};
